import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { PolymorphicHTMLProps } from '@findigs/helpers/src/helpers/polymorphic';
import { styles } from './icon-button.module.scss';

export const ICON_BUTTON_VARIANT = [
  'Standard',
  'StandardNavbar',
  'FilledStandard',
  'Outline',
  'Tonal',
] as const;

export type IconButtonVariant = (typeof ICON_BUTTON_VARIANT)[number];

export type IconButtonProps = Omit<PolymorphicHTMLProps, 'size'> & {
  /** @default 'Standard' */
  variant?: IconButtonVariant;

  /** @default 'Square' */
  shape?: 'Square' | 'Circle';

  /** @default 'Large' */
  size?: 'Small' | 'Large' | 'Nano';

  disabled?: boolean;

  /**
   * can be used to ignore `.active` styling
   * (helpful when using `polymorphic` with framework links)
   * @default false
   */
  ignoreActiveClass?: boolean;
};

/**
 * Extends the vanilla `button` element with our styling.
 * All vanilla `button` props are forwarded.
 *
 * This component is [polymorphic](/docs/component-system-polymorphic-components--docs).
 */
export const IconButton = polymorphic(
  { element: 'button', forwardRef: true },
  (
    {
      variant = 'Standard',
      shape = 'Square',
      size = 'Large',
      className,
      ignoreActiveClass = false,
      ...props
    }: IconButtonProps,
    Component,
    ref
  ) => {
    const classes = classList(styles, variant, shape, size, className, {
      ignoreActiveClass,
    });
    return (
      <Component
        {...props}
        className={classes}
        aria-disabled={(props['aria-disabled'] ?? props.disabled) || undefined}
        ref={ref}
      />
    );
  }
);
